import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  TooltipProps,
} from "recharts";
import { CHART_COLORS } from "./chartTokens";

export type ListingsPerDayDatum = {
  snapshotDate: string;
  totalListings: number;
  totalListingsIncludingMls: number;
  newLeads: number;
};

type Props = {
  title?: string;
  subtitle?: string;
  colSpan?: number;
  data: ListingsPerDayDatum[];
};

const formatNumber = (n: number) => n.toLocaleString("es-CO");

const roundingStep = (value: number) => {
  if (value <= 0) return 1;
  return Math.max(Math.pow(10, Math.floor(Math.log10(value)) - 1), 1);
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload as ListingsPerDayDatum;
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-semibold text-palomma-primary">{label}</p>
      <p className="text-sm text-palomma-secondary">
        Inmuebles: {formatNumber(row.totalListings)}
      </p>
    </div>
  );
};

const ListingsPerDayChart = ({
  title = "Evolución de inmuebles por día",
  subtitle = "Inmuebles disponibles por día · Eje vertical ajustado al rango",
  colSpan = 1,
  data,
}: Props) => {
  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {data.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Aún no hay snapshots disponibles para el rango seleccionado.
          </div>
        ) : (
          <div className="h-[260px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis
                  dataKey="snapshotDate"
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  tickMargin={10}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                />
                <YAxis
                  domain={[
                    (dataMin: number) => {
                      const step = roundingStep(dataMin);
                      return Math.max(0, Math.floor((dataMin - dataMin * 0.02) / step) * step);
                    },
                    (dataMax: number) => {
                      const step = roundingStep(dataMax);
                      return Math.ceil((dataMax + dataMax * 0.02) / step) * step;
                    },
                  ]}
                  allowDataOverflow={false}
                  tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                  axisLine={{ stroke: CHART_COLORS.tooltipBorder }}
                  allowDecimals={false}
                  tickFormatter={formatNumber}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="totalListings"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  dot={{ r: 3, fill: CHART_COLORS.primary }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingsPerDayChart;
